import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { SketchPicker } from "react-color";
import { Trash2, Plus } from "lucide-react";
import CustomSelect from "../components/common/CustomSelect";
import { CancelButtonForm, UpdateButtonForm } from "../components/ui/Button";
import { handleCustomResponse } from "../utils/response";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "../components/ui/LoadingComponent";
import ErrorAlert from "../components/ui/ErrorAlert";

const fieldTypeOptions = [
  { label: "Alpha (Letters)", value: "alpha" },
  { label: "Numeric (Numbers)", value: "numeric" },
  { label: "Static (Fixed Text)", value: "static" },
  { label: "Dropdown (Select)", value: "dropdown" },
];

const labelOptions = [
  { label: "Series Letter", value: "series_letter" },
  { label: "Numbers", value: "numbers" },
  { label: "Prefix", value: "prefix" },
  { label: "Suffix", value: "suffix" },
  { label: "Separator", value: "separator" },
];

const letterOptions = Array.from("ABCDEFGHIJKLMNOPQRSTUVWXYZ").map(
  (letter) => ({
    label: letter,
    value: letter,
  })
);

const labelValueMap = labelOptions.reduce((acc, opt) => {
  acc[opt.label] = opt.value;
  return acc;
}, {});

const EditPlateAuthoritiesPage = () => {
  const { authorityId } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPlateColorPicker, setShowPlateColorPicker] = useState(false);
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [showBorderColorPicker, setShowBorderColorPicker] = useState(false);
  const [authorityData, setAuthorityData] = useState(null);
  const [fields, setFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [error, setError] = useState(null);

  const countryOptions = countries.map((country) => ({
    label: country.country_name,
    value: country.country_id,
  }));

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      authority_name: "",
      format_pattern: "",
      color_hex: "#FFFFFF",
      text_color_hex: "#000000",
      border_color_hex: "#000000",
      emblem: "",
      country_id: null,
    },
  });

  const fetchCountries = async (search = "") => {
    try {
      setCountriesLoading(true);
      setErrorMessage("");
      const queryParams = new URLSearchParams();
      queryParams.append("want_pagination", 0);
      if (search) queryParams.append("search", search);

      const response = await apiCall(
        "get",
        `/get-all-countries?${queryParams.toString()}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        const countryList = response.data.data || [];
        const sortedCountries = countryList.sort((a, b) =>
          (a.country_name || "").localeCompare(b.country_name || "")
        );
        setCountries(sortedCountries);
      } else {
        const errorMsg = response?.data?.message || "Failed to load countries.";
        setErrorMessage(errorMsg);
        handleCustomResponse("error", "Failed to load countries.");
      }
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message || "Error fetching countries.";
      setErrorMessage(errorMsg);
      handleCustomResponse("error", errorMsg);
    } finally {
      setCountriesLoading(false);
    }
  };

  const fetchAuthority = async () => {
    try {
      setError(null);
      setIsLoading(true);
      const response = await apiCall(
        "get",
        `/get-plate-authority/${authorityId}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        const data = response.data.data;
        setValue("authority_name", data.authority_name);
        setValue("format_pattern", data.format_pattern);
        setValue("color_hex", data.color_hex);
        setValue("text_color_hex", data.text_color_hex);
        setValue("border_color_hex", data.border_color_hex);
        setValue("emblem", data.emblem);

        const selectedCountry = countryOptions.find(
          (opt) => opt.value === data.country_id
        );
        if (selectedCountry) {
          setValue("country_id", selectedCountry);
        }

        const mappedFields = (data.fields || []).map((field) => ({
          id: Date.now() + Math.random(),
          field_id: field.field_id,
          label:
            labelValueMap[field.label] ||
            field.label.toLowerCase().replace(/ /g, "_"),
          type:
            fieldTypeOptions.find((opt) => opt.value === field.type) || null,
          max_length: field.max_length.toString(),
          options: field.options || (field.type === "static" ? [] : [""]),
          value: field.value || "",
        }));
        setFields(mappedFields);

        setAuthorityData({
          authority_id: data.authority_id,
          authority_name: data.authority_name,
          format_pattern: data.format_pattern,
          color_hex: data.color_hex,
          text_color_hex: data.text_color_hex,
          border_color_hex: data.border_color_hex,
          emblem: data.emblem,
          country_name: selectedCountry?.label || "",
          is_active: data.is_active,
        });
      } else {
        handleCustomResponse("error", "Failed to load authority data.");
      }
    } catch (error) {
      handleCustomResponse("error", "Error fetching authority data.");
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (countries.length > 0 && authorityId) {
      fetchAuthority();
    }
  }, [countries, authorityId]);

  const validateStep = async (step) => {
    switch (step) {
      case 1: {
        const values = watch();
        if (
          !values.authority_name ||
          !values.format_pattern ||
          !values.emblem ||
          !values.country_id
        ) {
          handleCustomResponse("error", "Please fill all required fields.");
          return false;
        }
        if (!/^[A-Za-z0-9-]+$/.test(values.format_pattern)) {
          handleCustomResponse(
            "error",
            "Format pattern can only contain letters, numbers, and hyphens."
          );
          return false;
        }
        if (!/^#[0-9A-Fa-f]{6}$/.test(values.color_hex)) {
          handleCustomResponse("error", "Invalid plate color hex code.");
          return false;
        }
        if (!/^#[0-9A-Fa-f]{6}$/.test(values.text_color_hex)) {
          handleCustomResponse("error", "Invalid text color hex code.");
          return false;
        }
        if (!/^#[0-9A-Fa-f]{6}$/.test(values.border_color_hex)) {
          handleCustomResponse("error", "Invalid border color hex code.");
          return false;
        }
        return true;
      }
      case 2: {
        if (fields.length > 0) {
          for (let field of fields) {
            if (!field.label) {
              handleCustomResponse("error", "All fields must have a label.");
              return false;
            }
            if (!field.type) {
              handleCustomResponse("error", "All fields must have a type.");
              return false;
            }
            if (!field.max_length || field.max_length <= 0) {
              handleCustomResponse(
                "error",
                "All fields must have a valid max length."
              );
              return false;
            }
            if (field.type.value === "static" && !field.value.trim()) {
              handleCustomResponse("error", "Static fields must have a value.");
              return false;
            }
          }
        }
        return true;
      }
      case 3:
        return true;
      default:
        return true;
    }
  };

  const handleNextStep = async () => {
    const isValid = await validateStep(currentStep);
    if (isValid) {
      if (currentStep === 1) {
        const data = watch();
        setAuthorityData({
          ...authorityData,
          authority_name: data.authority_name,
          format_pattern: data.format_pattern,
          color_hex: data.color_hex,
          text_color_hex: data.text_color_hex,
          border_color_hex: data.border_color_hex,
          emblem: data.emblem,
          country_name: data.country_id.label,
        });
        setCurrentStep(2);
      } else if (currentStep === 2) {
        setCurrentStep(3);
      } else if (currentStep === 3) {
        handleFinish();
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const addField = () => {
    setFields([
      ...fields,
      {
        id: Date.now(),
        label: "",
        type: null,
        max_length: "",
        options: [""],
        value: "",
      },
    ]);
  };

  const removeField = (id) => {
    setFields(fields.filter((field) => field.id !== id));
  };

  const updateField = (id, property, value) => {
    setFields(
      fields.map((field) =>
        field.id === id ? { ...field, [property]: value } : field
      )
    );
  };

  const addOption = (fieldId) => {
    setFields(
      fields.map((field) =>
        field.id === fieldId
          ? { ...field, options: [...field.options, ""] }
          : field
      )
    );
  };

  const updateOption = (fieldId, optionIndex, value) => {
    setFields(
      fields.map((field) =>
        field.id === fieldId
          ? {
              ...field,
              options: field.options.map((opt, idx) =>
                idx === optionIndex ? value : opt
              ),
            }
          : field
      )
    );
  };

  const removeOption = (fieldId, optionIndex) => {
    setFields(
      fields.map((field) =>
        field.id === fieldId
          ? {
              ...field,
              options: field.options.filter((_, idx) => idx !== optionIndex),
            }
          : field
      )
    );
  };

  const handleSkipFields = () => {
    setShowConfirmModal(true);
  };

  const confirmSubmission = () => {
    setShowConfirmModal(false);
    setCurrentStep(3);
  };

  const handleFinish = async () => {
    setIsLoading(true);
    try {
      const formData = watch();
      const formattedFields = fields.map((field) => ({
        ...(field.field_id ? { field_id: field.field_id } : {}),
        label:
          labelOptions.find((opt) => opt.value === field.label)?.label ||
          field.label,
        type: field.type.value,
        max_length: parseInt(field.max_length),
        ...(field.type.value === "static" && { value: field.value }),
        ...((field.type.value === "alpha" || field.type.value === "dropdown") &&
          field.options.filter((opt) => opt.trim()).length > 0 && {
            options: field.options.filter((opt) => opt.trim()),
          }),
      }));

      const payload = {
        authority_id: parseInt(authorityId),
        country_id: parseInt(formData.country_id.value),
        authority_name: formData.authority_name,
        format_pattern: formData.format_pattern,
        color_hex: formData.color_hex,
        text_color_hex: formData.text_color_hex,
        border_color_hex: formData.border_color_hex,
        emblem: formData.emblem,
        is_active: authorityData?.is_active ?? true,
        fields: formattedFields,
      };

      const response = await apiCall(
        "post",
        "/update-plate-authority",
        payload,
        navigate
      );

      if (response.data.success) {
        handleCustomResponse("success", "Authority updated successfully!");
        navigate("/plate-authorities");
      } else {
        handleCustomResponse(
          "error",
          response.data.message || "Failed to update authority!"
        );
      }
    } catch (err) {
      console.error("Error updating authority:", err);
      let errorMessage = "Failed to update authority!";
      if (typeof err === "string") {
        errorMessage = err;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.error) {
        errorMessage = err.error;
      }
      handleCustomResponse("error", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  if (error) {
    return <ErrorAlert />;
  }
  return (
    <>
      <style>
        {`
        .container {
          width: 100% !important;
          max-width: 100% !important;
          padding-left: 15px;
          padding-right: 15px;
          margin: 0 auto;
        }
        .card {
          width: 100%;
          max-width: 100%;
        }
        .form-wizard {
          width: 100%;
        }
        @media (max-width: 576px) {
          .container {
            padding-left: 10px;
            padding-right: 10px;
          }
        }
        .modal-dialog {
          max-width: min(400px, 90vw) !important;
          margin: 1rem auto !important;
        }
        .color-picker-container {
          position: relative;
        }
        .color-swatch {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          border: 1px solid #e5e7eb;
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
        }
        `}
      </style>
      <div className="container">
        <div className="col">
          <div className="">
            <div className="card">
              <div className="card-body">
                <h6 className="mb-4 text-xl">Edit Plate Authority</h6>
                <p className="text-neutral-500">
                  Update the details and proceed through the steps.
                </p>

                <div className="form-wizard">
                  <form>
                    <div className="form-wizard-header overflow-x-auto scroll-sm pb-8 my-32">
                      <ul className="list-unstyled form-wizard-list style-two">
                        <li
                          className={`form-wizard-list__item ${
                            [2, 3].includes(currentStep) && "activated"
                          } ${currentStep === 1 && "active"} `}
                        >
                          <div className="form-wizard-list__line">
                            <span className="count">1</span>
                          </div>
                          <span className="text text-xs fw-semibold">
                            Authority Details
                          </span>
                        </li>
                        <li
                          className={`form-wizard-list__item ${
                            [3].includes(currentStep) && "activated"
                          } ${currentStep === 2 && "active"} `}
                        >
                          <div className="form-wizard-list__line">
                            <span className="count">2</span>
                          </div>
                          <span className="text text-xs fw-semibold">
                            Edit Fields
                          </span>
                        </li>
                        <li
                          className={`form-wizard-list__item ${
                            currentStep === 3 && "active"
                          } `}
                        >
                          <div className="form-wizard-list__line">
                            <span className="count">3</span>
                          </div>
                          <span className="text text-xs fw-semibold">
                            Review & Finish
                          </span>
                        </li>
                      </ul>
                    </div>

                    {/* Step 1: Authority Details */}
                    <fieldset
                      className={`wizard-fieldset ${
                        currentStep === 1 && "show"
                      } `}
                    >
                      <h6 className="text-md text-neutral-500">
                        Authority Details
                      </h6>
                      <div className="row gy-3">
                        <div className="col-12">
                          <label className="form-label">Country *</label>
                          <div className="position-relative">
                            <Controller
                              name="country_id"
                              control={control}
                              rules={{ required: "Country is required" }}
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  name="country_id"
                                  value={field.value}
                                  onChange={field.onChange}
                                  options={countryOptions}
                                  placeholder={
                                    countriesLoading
                                      ? "Loading countries..."
                                      : "Select Country"
                                  }
                                  isClearable
                                  isLoading={countriesLoading}
                                  noOptionsMessage={() => (
                                    <div>
                                      {errorMessage || "No countries available"}
                                    </div>
                                  )}
                                />
                              )}
                            />
                            <div className="wizard-form-error">
                              {errors.country_id && (
                                <span className="text-danger-600">
                                  {errors.country_id.message}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-12">
                          <label className="form-label">Authority Name *</label>
                          <div className="position-relative">
                            <input
                              id="authority_name"
                              type="text"
                              {...register("authority_name", {
                                required: "Authority name is required",
                                validate: (value) =>
                                  value.trim() !== "" ||
                                  "Authority name cannot be empty",
                              })}
                              className="form-control wizard-required"
                              placeholder="Enter authority name"
                            />
                            <div className="wizard-form-error">
                              {errors.authority_name && (
                                <span className="text-danger-600">
                                  {errors.authority_name.message}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-12">
                          <label className="form-label">Format Pattern *</label>
                          <div className="position-relative">
                            <input
                              id="format_pattern"
                              type="text"
                              {...register("format_pattern", {
                                required: "Format pattern is required",
                                pattern: {
                                  value: /^[A-Za-z0-9-]+$/,
                                  message:
                                    "Format pattern can only contain letters, numbers, and hyphens",
                                },
                              })}
                              className="form-control wizard-required"
                              placeholder="Enter format pattern (e.g., TX-1234)"
                            />
                            <div className="wizard-form-error">
                              {errors.format_pattern && (
                                <span className="text-danger-600">
                                  {errors.format_pattern.message}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-12">
                          <label className="form-label">Emblem *</label>
                          <div className="position-relative">
                            <input
                              id="emblem"
                              type="text"
                              {...register("emblem", {
                                required: "Emblem is required",
                                validate: (value) =>
                                  value.trim() !== "" ||
                                  "Emblem cannot be empty",
                              })}
                              className="form-control wizard-required"
                              placeholder="Enter emblem (e.g., Ontario Trillium Taxi)"
                            />
                            <div className="wizard-form-error">
                              {errors.emblem && (
                                <span className="text-danger-600">
                                  {errors.emblem.message}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-sm-4">
                          <label className="form-label">Plate Color *</label>
                          <div className="position-relative color-picker-container">
                            <input
                              id="color_hex"
                              type="text"
                              {...register("color_hex", {
                                required: "Plate color hex code is required",
                                pattern: {
                                  value: /^#[0-9A-Fa-f]{6}$/,
                                  message:
                                    "Invalid color hex code (e.g., #FFFF00)",
                                },
                              })}
                              className="form-control wizard-required"
                              placeholder="#FFFF00"
                              readOnly
                              onClick={() =>
                                setShowPlateColorPicker(!showPlateColorPicker)
                              }
                            />
                            <div
                              className="color-swatch"
                              style={{ backgroundColor: watch("color_hex") }}
                              onClick={() =>
                                setShowPlateColorPicker(!showPlateColorPicker)
                              }
                            ></div>
                            {showPlateColorPicker && (
                              <div className="absolute z-10 mt-2">
                                <SketchPicker
                                  color={watch("color_hex")}
                                  onChangeComplete={(color) => {
                                    setValue("color_hex", color.hex, {
                                      shouldValidate: true,
                                    });
                                    setShowPlateColorPicker(false);
                                  }}
                                />
                              </div>
                            )}
                            <div className="wizard-form-error">
                              {errors.color_hex && (
                                <span className="text-danger-600">
                                  {errors.color_hex.message}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-sm-4">
                          <label className="form-label">Text Color *</label>
                          <div className="position-relative color-picker-container">
                            <input
                              id="text_color_hex"
                              type="text"
                              {...register("text_color_hex", {
                                required: "Text color hex code is required",
                                pattern: {
                                  value: /^#[0-9A-Fa-f]{6}$/,
                                  message:
                                    "Invalid text color hex code (e.g., #000000)",
                                },
                              })}
                              className="form-control wizard-required"
                              placeholder="#000000"
                              readOnly
                              onClick={() =>
                                setShowTextColorPicker(!showTextColorPicker)
                              }
                            />
                            <div
                              className="color-swatch"
                              style={{
                                backgroundColor: watch("text_color_hex"),
                              }}
                              onClick={() =>
                                setShowTextColorPicker(!showTextColorPicker)
                              }
                            ></div>
                            {showTextColorPicker && (
                              <div className="absolute z-10 mt-2">
                                <SketchPicker
                                  color={watch("text_color_hex")}
                                  onChangeComplete={(color) => {
                                    setValue("text_color_hex", color.hex, {
                                      shouldValidate: true,
                                    });
                                    setShowTextColorPicker(false);
                                  }}
                                />
                              </div>
                            )}
                            <div className="wizard-form-error">
                              {errors.text_color_hex && (
                                <span className="text-danger-600">
                                  {errors.text_color_hex.message}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-sm-4">
                          <label className="form-label">Border Color *</label>
                          <div className="position-relative color-picker-container">
                            <input
                              id="border_color_hex"
                              type="text"
                              {...register("border_color_hex", {
                                required: "Border color hex code is required",
                                pattern: {
                                  value: /^#[0-9A-Fa-f]{6}$/,
                                  message:
                                    "Invalid border color hex code (e.g., #000000)",
                                },
                              })}
                              className="form-control wizard-required"
                              placeholder="#000000"
                              readOnly
                              onClick={() =>
                                setShowBorderColorPicker(!showBorderColorPicker)
                              }
                            />
                            <div
                              className="color-swatch"
                              style={{
                                backgroundColor: watch("border_color_hex"),
                              }}
                              onClick={() =>
                                setShowBorderColorPicker(!showBorderColorPicker)
                              }
                            ></div>
                            {showBorderColorPicker && (
                              <div className="absolute z-10 mt-2">
                                <SketchPicker
                                  color={watch("border_color_hex")}
                                  onChangeComplete={(color) => {
                                    setValue("border_color_hex", color.hex, {
                                      shouldValidate: true,
                                    });
                                    setShowBorderColorPicker(false);
                                  }}
                                />
                              </div>
                            )}
                            <div className="wizard-form-error">
                              {errors.border_color_hex && (
                                <span className="text-danger-600">
                                  {errors.border_color_hex.message}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="form-group d-flex align-items-center justify-content-end gap-8">
                          <CancelButtonForm
                            onClick={() => navigate("/plate-authorities")}
                          />
                          <button
                            type="button"
                            className="form-wizard-next-btn btn btn-primary-600 px-32"
                            onClick={handleNextStep}
                            disabled={isLoading || countriesLoading}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </fieldset>

                    {/* Step 2: Add Fields */}
                    <fieldset
                      className={`wizard-fieldset ${
                        currentStep === 2 && "show"
                      } `}
                    >
                      <h6 className="text-md text-neutral-500">Edit Fields</h6>
                      <div className="row gy-3">
                        <div className="col-12">
                          <div className="alert alert-info mb-4">
                            <strong>Authority:</strong>{" "}
                            {authorityData?.authority_name}
                            <br />
                            <strong>Format Pattern:</strong>{" "}
                            {authorityData?.format_pattern}
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Fields</h6>
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={addField}
                              disabled={fieldsLoading}
                            >
                              <Plus size={16} className="me-2" />
                              Add Field
                            </button>
                          </div>
                          {fields.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                              <p>
                                No fields added yet. Click "Add Field" to get
                                started or skip to finish.
                              </p>
                            </div>
                          ) : (
                            fields.map((field, index) => (
                              <div
                                key={field.id}
                                className="card mb-3 p-3 border"
                              >
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <h6 className="mb-0">Field {index + 1}</h6>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => removeField(field.id)}
                                    disabled={fieldsLoading}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                                <div className="row gy-3">
                                  <div className="col-md-4">
                                    <label className="form-label">
                                      Label *
                                    </label>
                                    <CustomSelect
                                      value={
                                        labelOptions.find(
                                          (option) =>
                                            option.value === field.label
                                        ) || null
                                      }
                                      onChange={(selected) =>
                                        updateField(
                                          field.id,
                                          "label",
                                          selected?.value || ""
                                        )
                                      }
                                      options={labelOptions}
                                      placeholder="Select Label"
                                    />
                                  </div>
                                  <div className="col-md-4">
                                    <label className="form-label">Type *</label>
                                    <CustomSelect
                                      value={field.type}
                                      onChange={(value) =>
                                        updateField(field.id, "type", value)
                                      }
                                      options={fieldTypeOptions}
                                      placeholder="Select Type"
                                    />
                                  </div>
                                  <div className="col-md-4">
                                    <label className="form-label">
                                      Max Length *
                                    </label>
                                    <input
                                      type="number"
                                      className="form-control wizard-required"
                                      placeholder="e.g., 5"
                                      min="1"
                                      value={field.max_length}
                                      onChange={(e) =>
                                        updateField(
                                          field.id,
                                          "max_length",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                  {field.type?.value === "static" && (
                                    <div className="col-12">
                                      <label className="form-label">
                                        Static Value *
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control wizard-required"
                                        placeholder="Enter fixed value"
                                        value={field.value}
                                        onChange={(e) =>
                                          updateField(
                                            field.id,
                                            "value",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>
                                  )}
                                  {(field.type?.value === "alpha" ||
                                    field.type?.value === "dropdown") && (
                                    <div className="col-12">
                                      <label className="form-label">
                                        Options{" "}
                                      </label>
                                      {field.label === "series_letter" ? (
                                        <CustomSelect
                                          isMulti={true}
                                          options={letterOptions}
                                          value={letterOptions.filter(
                                            (option) =>
                                              field.options.includes(
                                                option.value
                                              )
                                          )}
                                          onChange={(selected) => {
                                            const selectedValues = selected
                                              ? selected.map((opt) => opt.value)
                                              : [];
                                            updateField(
                                              field.id,
                                              "options",
                                              selectedValues
                                            );
                                          }}
                                          placeholder="Select Letters (e.g., A, B, C)"
                                        />
                                      ) : field.label === "numbers" ? (
                                        <CustomSelect
                                          isMulti={true}
                                          options={Array.from(
                                            { length: 10 },
                                            (_, i) => ({
                                              label: i.toString(),
                                              value: i.toString(),
                                            })
                                          )}
                                          value={Array.from(
                                            { length: 10 },
                                            (_, i) =>
                                              field.options.includes(
                                                i.toString()
                                              )
                                                ? {
                                                    label: i.toString(),
                                                    value: i.toString(),
                                                  }
                                                : null
                                          ).filter(Boolean)}
                                          onChange={(selected) => {
                                            const selectedValues = selected
                                              ? selected.map((opt) => opt.value)
                                              : [];
                                            updateField(
                                              field.id,
                                              "options",
                                              selectedValues
                                            );
                                          }}
                                          placeholder="Select Digits (e.g., 0, 1, 2)"
                                        />
                                      ) : field.label === "prefix" ||
                                        field.label === "suffix" ? (
                                        <CustomSelect
                                          options={[
                                            { label: "M", value: "M" },
                                            { label: "L", value: "L" },
                                            { label: "X", value: "X" },
                                            { label: "T", value: "T" },
                                          ]}
                                          value={
                                            field.options[0]
                                              ? {
                                                  label: field.options[0],
                                                  value: field.options[0],
                                                }
                                              : null
                                          }
                                          onChange={(selected) =>
                                            updateField(field.id, "options", [
                                              selected?.value || "",
                                            ])
                                          }
                                          placeholder="Select Character (e.g., M, L)"
                                        />
                                      ) : field.label === "separator" ? (
                                        <CustomSelect
                                          options={[
                                            { label: "Dash (-)", value: "-" },
                                            { label: "Space", value: " " },
                                          ]}
                                          value={
                                            field.options[0]
                                              ? {
                                                  label:
                                                    field.options[0] === " "
                                                      ? "Space"
                                                      : "Dash (-)",
                                                  value: field.options[0],
                                                }
                                              : null
                                          }
                                          onChange={(selected) =>
                                            updateField(field.id, "options", [
                                              selected?.value || "",
                                            ])
                                          }
                                          placeholder="Select Separator (e.g., -, space)"
                                        />
                                      ) : (
                                        <>
                                          {field.options.map(
                                            (option, optIdx) => (
                                              <div
                                                key={optIdx}
                                                className="d-flex gap-2 mb-2"
                                              >
                                                <input
                                                  type="text"
                                                  className="form-control"
                                                  placeholder={`Option ${
                                                    optIdx + 1
                                                  }`}
                                                  value={option}
                                                  onChange={(e) =>
                                                    updateOption(
                                                      field.id,
                                                      optIdx,
                                                      e.target.value
                                                    )
                                                  }
                                                />
                                                {field.options.length > 1 && (
                                                  <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() =>
                                                      removeOption(
                                                        field.id,
                                                        optIdx
                                                      )
                                                    }
                                                    disabled={fieldsLoading}
                                                  >
                                                    <Trash2 size={16} />
                                                  </button>
                                                )}
                                              </div>
                                            )
                                          )}
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-secondary"
                                            onClick={() => addOption(field.id)}
                                            disabled={fieldsLoading}
                                          >
                                            <Plus size={14} className="me-1" />
                                            Add Option
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                        <div className="form-group d-flex align-items-center justify-content-end gap-8">
                          <button
                            type="button"
                            className="form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32"
                            onClick={prevStep}
                            disabled={isLoading || fieldsLoading}
                          >
                            Back
                          </button>
                          <button
                            type="button"
                            className="form-wizard-next-btn btn btn-primary-600 px-32"
                            onClick={handleSkipFields}
                            disabled={isLoading || fieldsLoading}
                          >
                            Skip Fields
                          </button>
                          <button
                            type="button"
                            className="form-wizard-next-btn btn btn-primary-600 px-32"
                            onClick={handleNextStep}
                            disabled={isLoading || fieldsLoading}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </fieldset>

                    {/* Step 3: Review & Finish */}
                    <fieldset
                      className={`wizard-fieldset ${
                        currentStep === 3 && "show"
                      } `}
                    >
                      <h6 className="text-md text-neutral-500">
                        Review Information
                      </h6>
                      <div className="row gy-3">
                        <div className="col-12">
                          <div className="card border-neutral-100">
                            <div className="card-body">
                              <h6 className="text-md text-neutral-500">
                                Authority Details
                              </h6>
                              <div className="row mb-2">
                                <div className="col-6 text-neutral-500">
                                  Authority Name:
                                </div>
                                <div className="col-6">
                                  {authorityData?.authority_name}
                                </div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-6 text-neutral-500">
                                  Country:
                                </div>
                                <div className="col-6">
                                  {authorityData?.country_name}
                                </div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-6 text-neutral-500">
                                  Format Pattern:
                                </div>
                                <div className="col-6">
                                  {authorityData?.format_pattern}
                                </div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-6 text-neutral-500">
                                  Emblem:
                                </div>
                                <div className="col-6">
                                  {authorityData?.emblem}
                                </div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-6 text-neutral-500">
                                  Plate Color:
                                </div>
                                <div className="col-6 d-flex align-items-center">
                                  <div
                                    className="w-6 h-6 rounded-sm border border-gray-300 me-2"
                                    style={{
                                      backgroundColor: authorityData?.color_hex,
                                    }}
                                  ></div>
                                  {authorityData?.color_hex}
                                </div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-6 text-neutral-500">
                                  Text Color:
                                </div>
                                <div className="col-6 d-flex align-items-center">
                                  <div
                                    className="w-6 h-6 rounded-sm border border-gray-300 me-2"
                                    style={{
                                      backgroundColor:
                                        authorityData?.text_color_hex,
                                    }}
                                  ></div>
                                  {authorityData?.text_color_hex}
                                </div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-6 text-neutral-500">
                                  Border Color:
                                </div>
                                <div className="col-6 d-flex align-items-center">
                                  <div
                                    className="w-6 h-6 rounded-sm border border-gray-300 me-2"
                                    style={{
                                      backgroundColor:
                                        authorityData?.border_color_hex,
                                    }}
                                  ></div>
                                  {authorityData?.border_color_hex}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {fields.length > 0 && (
                          <div className="col-12">
                            <div className="card border-neutral-100">
                              <div className="card-body">
                                <h6 className="text-md text-neutral-500">
                                  Fields
                                </h6>
                                {fields.map((field, index) => (
                                  <div key={field.id} className="row mb-2">
                                    <div className="col-6 text-neutral-500">
                                      Field {index + 1}:
                                    </div>
                                    <div className="col-6">
                                      <strong>Label:</strong>{" "}
                                      {labelOptions.find(
                                        (opt) => opt.value === field.label
                                      )?.label || field.label}
                                      <br />
                                      <strong>Type:</strong> {field.type?.label}
                                      <br />
                                      <strong>Max Length:</strong>{" "}
                                      {field.max_length}
                                      <br />
                                      {field.type?.value === "static" && (
                                        <>
                                          <strong>Value:</strong> {field.value}
                                          <br />
                                        </>
                                      )}
                                      {(field.type?.value === "alpha" ||
                                        field.type?.value === "dropdown") && (
                                        <>
                                          <strong>Options:</strong>{" "}
                                          {field.options
                                            .filter((opt) => opt.trim())
                                            .join(", ")}
                                          <br />
                                        </>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="form-group d-flex align-items-center justify-content-end gap-8">
                          <button
                            type="button"
                            className="form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32"
                            onClick={prevStep}
                            disabled={isLoading || fieldsLoading}
                          >
                            Back
                          </button>
                          <button
                            type="button"
                            className="form-wizard-next-btn btn btn-primary-600 px-32"
                            onClick={handleNextStep}
                            disabled={isLoading || fieldsLoading}
                          >
                            Finish
                          </button>
                        </div>
                      </div>
                    </fieldset>
                  </form>
                </div>
              </div>
            </div>

            {/* Confirmation Modal */}
            <div
              className={`modal fade ${showConfirmModal ? "show d-block" : ""}`}
              style={{
                background: showConfirmModal ? "rgba(0,0,0,0.5)" : "none",
              }}
              tabIndex="-1"
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Confirm Skip Fields</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowConfirmModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    Are you sure you want to skip editing fields and proceed to
                    finish?
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-neutral-500 border-neutral-100"
                      onClick={() => setShowConfirmModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary-600"
                      onClick={confirmSubmission}
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Loading Overlay */}
            {/* {(isLoading || fieldsLoading || countriesLoading) && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ background: "rgba(0,0,0,0.6)", zIndex: 9999 }}
              >
                <LoadingComponent />
              </div>
            )} */}
          </div>
        </div>
      </div>
    </>
  );
};

export default EditPlateAuthoritiesPage;
